import { device } from './device'
import type {
  Me,
  BabyRecord,
  RecordType,
  DiaperColor,
  DiaperTexture,
  InviteResult,
  ExportData,
  PhotoUploadResult,
  StatsResponse,
  AiQuota,
} from './types'

const BASE = '/api'

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = device.getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(BASE + path, { ...init, headers })
  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      throw new ApiError(`响应解析失败 (${res.status})`, res.status)
    }
  }

  if (!res.ok) {
    const msg = (data as { error?: string })?.error || `HTTP ${res.status}`
    throw new ApiError(msg, res.status)
  }
  return data as T
}

export interface JoinResponse {
  family: Me['family']
  device: Me['device']
  device_token: string
  devices: Me['devices']
  ai_configured: boolean
  ai_vision_configured: boolean
  photos_enabled: boolean
  r2_usage_bytes: number
}

async function uploadFile<T>(path: string, formData: FormData): Promise<T> {
  const token = device.getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(BASE + path, { method: 'POST', headers, body: formData })
  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      throw new ApiError(`响应解析失败 (${res.status})`, res.status)
    }
  }
  if (!res.ok) {
    const msg = (data as { error?: string })?.error || `HTTP ${res.status}`
    throw new ApiError(msg, res.status)
  }
  return data as T
}

export const api = {
  async me(): Promise<Me | null> {
    try {
      return await request<Me>('/me')
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null
      throw e
    }
  },

  createFamily(input: {
    familyName: string
    relationship: 'father' | 'mother' | 'other'
    deviceName?: string // 当 relationship='other' 时必填
    joinCode?: string
  }): Promise<JoinResponse> {
    return request<JoinResponse>('/family', {
      method: 'POST',
      body: JSON.stringify({
        family_name: input.familyName,
        relationship: input.relationship,
        device_name: input.deviceName,
        join_code: input.joinCode || undefined,
      }),
    })
  },

  lookupFamily(code: string): Promise<{ family_id: string; family_name: string; member_count: number }> {
    return request(`/family/lookup?code=${encodeURIComponent(code)}`)
  },

  joinFamily(input: {
    code: string
    familyName?: string // 可选，填了会覆盖家庭名
    relationship?: 'father' | 'mother' | 'other' // 可选，默认 'other'
    deviceName?: string
  }): Promise<JoinResponse> {
    return request<JoinResponse>('/family/join', {
      method: 'POST',
      body: JSON.stringify({
        code: input.code,
        family_name: input.familyName || undefined,
        relationship: input.relationship,
        device_name: input.deviceName,
      }),
    })
  },

  renameFamily(name: string): Promise<{ ok: true; family: { id: string; name: string } }> {
    return request('/family/rename', {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  },

  acceptInvite(token: string, deviceName?: string): Promise<JoinResponse> {
    return request<JoinResponse>('/invite/accept', {
      method: 'POST',
      body: JSON.stringify({ token, device_name: deviceName }),
    })
  },

  createInvite(): Promise<InviteResult> {
    return request<InviteResult>('/invite', { method: 'POST' })
  },

  listRecords(since?: number): Promise<{ records: BabyRecord[] }> {
    const q = since ? `?since=${since}` : ''
    return request<{ records: BabyRecord[] }>(`/records${q}`)
  },

  createRecord(input: {
    client_uuid: string
    type: RecordType
    occurred_at: number
    color?: DiaperColor
    texture?: DiaperTexture
    note?: string
    photo_id?: string
  }): Promise<{ record: BabyRecord; duplicate: boolean }> {
    return request('/records', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  deleteRecord(id: string): Promise<{ ok: true }> {
    return request(`/records/${id}`, { method: 'DELETE' })
  },

  // 上传照片（可关联到记录）
  async uploadPhoto(file: Blob, recordId?: string): Promise<PhotoUploadResult> {
    const fd = new FormData()
    fd.append('file', file, 'photo.jpg')
    if (recordId) fd.append('record_id', recordId)
    return uploadFile('/photos', fd)
  },

  photoUrl(id: string): string {
    return `/api/photos/${id}`
  },

  deletePhoto(id: string): Promise<{ ok: true }> {
    return request(`/photos/${id}`, { method: 'DELETE' })
  },

  revokeDevice(id: string): Promise<{ ok: true }> {
    return request(`/devices/${id}/revoke`, { method: 'POST' })
  },

  exportAll(): Promise<ExportData> {
    return request<ExportData>('/export')
  },

  stats(days: number = 7): Promise<StatsResponse> {
    return request<StatsResponse>(`/stats?days=${days}`)
  },

  // AI 视觉分析（需先上传照片并关联到 record）
  aiImage(recordId: string): Promise<{ result: unknown; quota: AiQuota }> {
    return request('/ai/image', {
      method: 'POST',
      body: JSON.stringify({ record_id: recordId }),
    })
  },

  // AI 趋势总结
  aiSummary(days: number = 7): Promise<{ result: string; stats: unknown }> {
    return request('/ai/summary', {
      method: 'POST',
      body: JSON.stringify({ days }),
    })
  },
}
